import React from 'react';
import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import { getTripScoreColor } from './trip-utils';

/**
 * Props for the TripScoreRow component.
 */
interface TripScoreRowProps {
	/** Label for the score category (e.g., "Smoothness", "Speed", "Usage"). */
	label: string;
	/** Normalized score value (0-100). */
	score: number;
	/** Optional description text explaining the score category. */
	description?: string;
	/** Whether to show the numeric score. Bars and tips still render when false. Default: false */
	showScore?: boolean;
}

/**
 * Individual score category row with label, value, progress bar, and description.
 *
 * Displays a trip score metric with the category label on the left, numeric
 * score on the right, a colored progress bar below, and optional descriptive
 * text. Used within TripScoreBreakdown to show smoothness, speed, and usage scores.
 *
 * @component
 * @example
 * ```tsx
 * <TripScoreRow
 *   label="Smoothness"
 *   score={78}
 *   description="Good acceleration and braking patterns."
 * />
 * ```
 */
const TripScoreRow: React.FC<TripScoreRowProps> = ({ label, score, description, showScore = false }) => {
	const theme = useTheme();
	const progressColor = getTripScoreColor(score, theme);

	return (
		<Box sx={{ mb: 2.5 }}>
			{/* Label and Score Row */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 0.75,
				}}
			>
				<Typography
					variant='body1'
					sx={{
						fontWeight: 'medium',
						color: theme.palette.text.primary,
					}}
				>
					{label}
				</Typography>
				{showScore && (
					<Typography
						variant='body1'
						sx={{
							fontWeight: 'bold',
							color: theme.palette.text.primary,
						}}
					>
						{Math.round(score)}
					</Typography>
				)}
			</Box>

			{/* Progress Bar */}
			<LinearProgress
				variant='determinate'
				value={score}
				sx={{
					height: 8,
					borderRadius: 4,
					backgroundColor: theme.palette.grey[200],
					'& .MuiLinearProgress-bar': {
						backgroundColor: progressColor,
						borderRadius: 4,
					},
				}}
			/>

			{/* Description */}
			{description && (
				<Typography
					variant='body2'
					sx={{
						color: theme.palette.text.secondary,
						mt: 1,
						lineHeight: 1.5,
					}}
				>
					{description}
				</Typography>
			)}
		</Box>
	);
};

export default TripScoreRow;
